import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  fetchCompaniesAdmin,
  fetchCompaniesFallback,
  testCompaniesApi,
  seedTestCompany,
  loadCompanies
} from '../../store/companies';

function CompanyList() {
  const dispatch = useDispatch();
  const history = useHistory();
  const companies = useSelector(state => state.companies);
  const user = useSelector(state => state.session.user);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiResponse, setApiResponse] = useState(null);
  const [testResponse, setTestResponse] = useState(null);
  const [localCompanies, setLocalCompanies] = useState([]);
  const [fallbackCompanies, setFallbackCompanies] = useState([]);
  const [showDebug, setShowDebug] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        console.log('Loading companies...');

        // First check if the API is working at all
        try {
          // Use Redux thunk for API test
          const testData = await dispatch(testCompaniesApi());
          console.log('API Test Response via Redux:', testData);
          setTestResponse(testData);

          // Use the sample data as a fallback if nothing else works
          if (testData && testData.count > 0 && testData.sample && testData.sample.length > 0) {
            // Check if we have complete data
            if (testData.complete_data && Array.isArray(testData.complete_data)) {
              console.log('Using complete company data from test endpoint');
              setFallbackCompanies(testData.complete_data);
            } else {
              // If complete data is not available, create basic company objects from the sample data
              const fallbackData = testData.sample.map(item => ({
                id: item.id,
                company_name: item.name,
                accounts: [],
                created_at: new Date().toISOString()
              }));
              setFallbackCompanies(fallbackData);
              console.log('Created fallback companies from sample:', fallbackData);
            }
          }

          // If we have companies but they're not showing up, we might have a Redux issue
          if (testData && testData.count > 0) {
            console.log(`Test endpoint shows ${testData.count} companies are available`);

            // Try to get the actual companies since we know they exist
            try {
              // Use the Redux thunk for fallback fetching
              const fallbackData = await dispatch(fetchCompaniesFallback());
              console.log('Companies data from fallback fetch:', fallbackData);

              // Set local state if we got valid data
              if (Array.isArray(fallbackData) && fallbackData.length > 0) {
                setLocalCompanies(fallbackData);
              }
            } catch (fullDataError) {
              console.error('Error fetching full company data:', fullDataError);
            }
          }
        } catch (testError) {
          console.error('Error testing API:', testError);
        }

        // Try the normal API endpoint via Redux
        try {
          // Only try Redux if we don't already have data
          if (localCompanies.length === 0) {
            const companiesData = await dispatch(fetchCompaniesAdmin());
            console.log('Companies loaded through Redux:', companiesData);
          }
        } catch (reduxError) {
          console.error('Error with Redux fetch:', reduxError);
        }
      } catch (error) {
        console.error('Error loading companies:', error);
        setError(error.toString());
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [dispatch]);

  // Display companies using the first available source:
  // 1. Redux store
  // 2. Local state from direct fetch
  // 3. Fallback companies created from test endpoint sample
  const displayCompanies = companies.length > 0
    ? companies
    : localCompanies.length > 0
      ? localCompanies
      : fallbackCompanies;

  useEffect(() => {
    console.log('Current companies in store:', companies);
    console.log('Current local companies:', localCompanies);
    console.log('Fallback companies:', fallbackCompanies);
    console.log('Display companies:', displayCompanies);
  }, [companies, localCompanies, fallbackCompanies, displayCompanies]);

  const handleAddCompany = () => {
    history.push('/companies/new');
  };

  const handleSeedTestCompany = async () => {
    try {
      // Use Redux thunk for seeding test company
      const result = await dispatch(seedTestCompany());

      if (result) {
        console.log('Seed test company response via Redux:', result);

        // Refresh the data without full page reload
        dispatch(fetchCompaniesAdmin());
      } else {
        setError('Failed to seed test company');
      }
    } catch (error) {
      console.error('Error seeding test company:', error);
      setError('Failed to seed test company: ' + error.toString());
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
          <div className="ml-4 text-2xl text-blue-600">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-5">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-6xl font-bold text-blue-600">Company List</h1>
        <div className="flex gap-4">
          {testResponse && testResponse.count === 0 && user?.is_admin && (
            <button
              onClick={handleSeedTestCompany}
              className="bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-6 rounded-lg text-2xl transition duration-200 ease-in-out transform hover:scale-105"
            >
              Seed Test Company
            </button>
          )}
          {user?.is_admin && (
            <button
              onClick={handleAddCompany}
              className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 px-6 rounded-lg text-2xl transition duration-200 ease-in-out transform hover:scale-105"
            >
              Add New Company
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6" role="alert">
          <p className="font-bold text-2xl">Error loading companies:</p>
          <p className="text-xl">{error}</p>
        </div>
      )}

      {/* Debug toggle */}
      <div className="mb-4 flex items-center">
        <label className="flex items-center cursor-pointer">
          <div className="relative">
            <input id="show-debug"
              type="checkbox"
              className="sr-only"
              checked={showDebug}
              onChange={() => setShowDebug(!showDebug)}
            />
            <div className={`block w-14 h-8 rounded-full transition ${showDebug ? 'bg-blue-500' : 'bg-gray-300'}`}></div>
            <div className={`absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition transform ${showDebug ? 'translate-x-6' : ''}`}></div>
          </div>
          <div className="ml-3 text-lg font-medium text-gray-700">Show Debug Info</div>
        </label>
      </div>

      {/* Debug info */}
      {showDebug && (
        <div className="mb-6 p-4 bg-gray-100 rounded-lg">
          <h2 className="text-2xl font-semibold mb-2">Debug Info</h2>
          <p className="text-lg mb-2">API Response: {apiResponse ? JSON.stringify(apiResponse) : 'None'}</p>
          <p className="text-lg mb-2">Test API Response: {testResponse ? JSON.stringify(testResponse) : 'None'}</p>
          <p className="text-lg mb-2">Redux Store Companies: {companies ? JSON.stringify(companies) : 'None'}</p>
          <p className="text-lg mb-2">Local Companies: {localCompanies ? JSON.stringify(localCompanies) : 'None'}</p>
          <p className="text-lg mb-2">Fallback Companies: {fallbackCompanies ? JSON.stringify(fallbackCompanies) : 'None'}</p>
          <p className="text-lg mb-2">Companies Type: {companies ? typeof companies : 'None'}</p>
          <p className="text-lg mb-2">Companies Length: {companies && Array.isArray(companies) ? companies.length : 'Not an array'}</p>
          <p className="text-lg mb-2">Current User: {user ? JSON.stringify(user) : 'Not logged in'}</p>

          {testResponse && testResponse.count > 0 && (
            <div className="mt-4 p-4 bg-yellow-100 rounded-lg">
              <h3 className="text-xl font-semibold mb-2">Test Endpoint Results</h3>
              <p className="text-lg">Found {testResponse.count} companies in the database.</p>
              <p className="text-lg">Sample: {JSON.stringify(testResponse.sample)}</p>
              <p className="text-lg">User Authenticated: {testResponse.current_user ? 'Yes' : 'No'}</p>
              <p className="text-lg">User is Admin: {testResponse.is_admin ? 'Yes' : 'No'}</p>
            </div>
          )}
        </div>
      )}

      {!displayCompanies.length ? (
        <div className="text-2xl text-gray-500 text-center p-8 bg-gray-100 rounded-lg">
          No companies found.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {displayCompanies.map((company) => (
            <div
              key={company.id}
              className="bg-white rounded-lg p-4 shadow hover:shadow-md transition-shadow border border-gray-200"
            >
              <div className="flex justify-between">
                <h2 className="text-2xl font-semibold mb-2">{company.company_name}</h2>
                {user?.is_admin && (
                  <button
                    onClick={() => history.push(`/companies/${company.id}/edit`)}
                    className="text-blue-500 hover:text-blue-700 text-xl"
                  >
                    Edit
                  </button>
                )}
              </div>

              <p className="text-lg">
                <span className="font-semibold">Accounts:</span>{' '}
                {company.accounts && company.accounts.length > 0
                  ? company.accounts.length
                  : 'No accounts'
                }
              </p>

              <p className="text-lg">
                <span className="font-semibold">Created:</span>{' '}
                {company.created_at ? new Date(company.created_at).toLocaleDateString() : 'Unknown'}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default CompanyList;
